import React from 'react';
import {
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';

import colors from '@atom/styles/colors';
import fonts from '@atom/styles/fonts';
import { ConversationalAnalyticsChartProps } from '@atom/types/conversationalAnalytics';
import {
  CHART_COLORS,
  CHART_HEIGHT,
} from '@atom/utilities/conversationalAnalyticsUtilities';

import useConversationalAnalyticsData from './hooks/useConversationalAnalyticsData';
import ConversationalAnalyticsChartTooltip from './ConversationalAnalyticsChartTooltip';
import ConversationalAnalyticsChartWrapper from './ConversationalAnalyticsChartWrapper';

const renderLabel = (entry: any, yField: string) => {
  const value = entry[yField];
  const formattedValue =
    typeof value === 'number' ? value.toLocaleString() : value;

  return (
    <text
      x={entry.x}
      y={entry.y}
      fill={colors.neutral.dim}
      fontSize={fonts.sm}
      textAnchor={entry.x > entry.cx ? 'start' : 'end'}
      dominantBaseline="central"
    >
      {formattedValue}
    </text>
  );
};

const ConversationalAnalyticsPieChart: React.FC<
  ConversationalAnalyticsChartProps
> = ({ spec, width = '100%', height, onExport }) => {
  const { chartData, xDisplayField, yField } =
    useConversationalAnalyticsData(spec);

  return (
    <ConversationalAnalyticsChartWrapper spec={spec} onExport={onExport}>
      <ResponsiveContainer width={width} height={height || CHART_HEIGHT}>
        <PieChart>
          <Pie
            data={chartData}
            dataKey={yField}
            nameKey={xDisplayField}
            cx="50%"
            cy="50%"
            outerRadius="80%"
            label={entry => renderLabel(entry, yField)}
            labelLine={{ stroke: colors.neutral.dim }}
          >
            {chartData.map((_, index) => (
              <Cell
                key={`cell-${index}`}
                fill={CHART_COLORS[index % CHART_COLORS.length]}
              />
            ))}
          </Pie>
          <Tooltip
            content={({ active, payload }) => (
              <ConversationalAnalyticsChartTooltip
                active={active}
                payload={payload}
                xDisplayField={xDisplayField}
              />
            )}
          />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    </ConversationalAnalyticsChartWrapper>
  );
};

export default ConversationalAnalyticsPieChart;
